import React, { useState, useEffect } from 'react';
import { useAuth } from '../AuthContext';

function AdminEmailTemplatesPage() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [saveStatus, setSaveStatus] = useState(null);
  const { token } = useAuth();

  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      setError(null);
      try {
        const response = await fetch('/api/admin/email-settings', {
          headers: { 'Authorization': `Bearer ${token}` },
        });
        if (!response.ok) throw new Error('Не удалось загрузить настройки почты');
        const res = await response.json();
        setData({
          mail_sender_name: res.mail_sender_name || '',
          verification_subject: res.verification_subject || 'Подтвердите email',
          verification_body_html: res.verification_body_html || '',
          broadcast_body_html: res.broadcast_body_html || '',
          default_verification_body_html: res.default_verification_body_html || '',
          default_broadcast_body_html: res.default_broadcast_body_html || '',
        });
      } catch (e) {
        setError(e.message);
      } finally {
        setLoading(false);
      }
    };
    if (token) fetchData();
  }, [token]);

  const handleChange = (field, value) => {
    setData((prev) => (prev ? { ...prev, [field]: value } : null));
  };

  const handleResetTemplate = (templateKey) => {
    if (!data) return;
    if (templateKey === 'verification' && data.default_verification_body_html) {
      setData((prev) => ({ ...prev, verification_body_html: data.default_verification_body_html }));
    }
    if (templateKey === 'broadcast' && data.default_broadcast_body_html) {
      setData((prev) => ({ ...prev, broadcast_body_html: data.default_broadcast_body_html }));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!data) return;
    setSaving(true);
    setError(null);
    setSaveStatus(null);
    try {
      const response = await fetch('/api/admin/email-settings', {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          mail_sender_name: data.mail_sender_name,
          verification_subject: data.verification_subject,
          verification_body_html: data.verification_body_html,
          broadcast_body_html: data.broadcast_body_html,
        }),
      });
      const res = await response.json();
      if (!response.ok) throw new Error(res.message || 'Ошибка сохранения');
      setSaveStatus('success');
      setTimeout(() => setSaveStatus(null), 2500);
    } catch (e) {
      setError(e.message);
      setSaveStatus('error');
    } finally {
      setSaving(false);
    }
  };

  if (loading || !data) {
    return (
      <main className="admin-page-content">
        <section><div className="loading-mini">Загрузка...</div></section>
      </main>
    );
  }

  return (
    <main className="admin-page-content">
      <section>
        <h2 className="admin-page-title">Шаблоны писем</h2>
        <p className="admin-description-text">
          Имя отправителя в поле «От кого» и HTML-шаблоны для письма подтверждения email и для рассылки.
          В шаблонах можно использовать переменные: в верификации — <code>{'{{ verification_url }}'}</code>,{' '}
          в рассылке — <code>{'{{ subject }}'}</code>, <code>{'{{ message }}'}</code>.
        </p>

        {error && <div className="message-error">{error}</div>}
        {saveStatus === 'success' && <div className="message-success">Настройки сохранены.</div>}
        {saveStatus === 'error' && <div className="message-error">Ошибка сохранения.</div>}

        <form onSubmit={handleSubmit}>
          <div className="settings-form-group">
            <label htmlFor="mail_sender_name" className="settings-label">
              Имя отправителя (поле «От кого»)
            </label>
            <input
              type="text"
              id="mail_sender_name"
              value={data.mail_sender_name}
              onChange={(e) => handleChange('mail_sender_name', e.target.value)}
              className="settings-input"
              placeholder="Панель"
              maxLength={100}
            />
            <p className="settings-hint">
              Если пусто — используется название сайта из брендинга или переменная MAIL_SENDER_NAME.
            </p>
          </div>

          <hr style={{ margin: '24px 0', border: 'none', borderTop: '1px solid var(--border-color)' }} />

          <h3 className="admin-section-title">Письмо подтверждения email</h3>
          <div className="settings-form-group">
            <label htmlFor="verification_subject" className="settings-label">
              Тема письма
            </label>
            <input
              type="text"
              id="verification_subject"
              value={data.verification_subject}
              onChange={(e) => handleChange('verification_subject', e.target.value)}
              className="settings-input"
              placeholder="Подтвердите email"
              maxLength={200}
            />
          </div>
          <div className="settings-form-group">
            <label htmlFor="verification_body_html" className="settings-label">
              HTML письма
            </label>
            <p className="settings-hint" style={{ marginBottom: '8px' }}>
              Переменная: <code>{'{{ verification_url }}'}</code> — ссылка для подтверждения.
            </p>
            <textarea
              id="verification_body_html"
              value={data.verification_body_html}
              onChange={(e) => handleChange('verification_body_html', e.target.value)}
              className="settings-input"
              placeholder="Оставьте пустым, чтобы использовать шаблон по умолчанию"
              rows={18}
              style={{ fontFamily: 'monospace', fontSize: '13px' }}
            />
            {data.default_verification_body_html && (
              <button
                type="button"
                className="btn btn-secondary"
                style={{ marginTop: '8px' }}
                onClick={() => handleResetTemplate('verification')}
              >
                Сбросить на шаблон по умолчанию
              </button>
            )}
          </div>

          <hr style={{ margin: '24px 0', border: 'none', borderTop: '1px solid var(--border-color)' }} />

          <h3 className="admin-section-title">Шаблон рассылки (broadcast)</h3>
          <div className="settings-form-group">
            <label htmlFor="broadcast_body_html" className="settings-label">
              HTML письма рассылки
            </label>
            <p className="settings-hint" style={{ marginBottom: '8px' }}>
              Переменные: <code>{'{{ subject }}'}</code> — тема, <code>{'{{ message }}'}</code> — текст сообщения.
            </p>
            <textarea
              id="broadcast_body_html"
              value={data.broadcast_body_html}
              onChange={(e) => handleChange('broadcast_body_html', e.target.value)}
              className="settings-input"
              placeholder="Оставьте пустым, чтобы использовать шаблон по умолчанию"
              rows={18}
              style={{ fontFamily: 'monospace', fontSize: '13px' }}
            />
            {data.default_broadcast_body_html && (
              <button
                type="button"
                className="btn btn-secondary"
                style={{ marginTop: '8px' }}
                onClick={() => handleResetTemplate('broadcast')}
              >
                Сбросить на шаблон по умолчанию
              </button>
            )}
          </div>

          <div className="settings-form-group" style={{ marginTop: '24px' }}>
            <button type="submit" className="btn btn-primary" disabled={saving}>
              {saving ? 'Сохранение...' : 'Сохранить'}
            </button>
          </div>
        </form>
      </section>
    </main>
  );
}

export default AdminEmailTemplatesPage;
